"use client";

import { useState } from "react";
import type { NormalizedEmail } from "@/lib/emailCleaner/types";
import { aiGateway, AiGatewayError } from "@/lib/emailCleaner/aiGateway";

type Props = { emails: NormalizedEmail[] };
type Turn = { role: "user" | "assistant"; content: string };

export default function AssistantPanel({ emails }: Props) {
  const [messages, setMessages] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setError(null);
    const nextMessages: Turn[] = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setLoading(true);
    try {
      const context = emails.slice(0, 40).map((e) => ({
        from: e.from,
        subject: e.subject,
        snippet: e.snippet,
        received_at: e.received_at,
        is_read: e.is_read,
      }));
      const { reply } = await aiGateway.chat(
        text,
        nextMessages.map((m) => ({ role: m.role, content: m.content })),
        context as unknown as NormalizedEmail[],
      );
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setError(err instanceof AiGatewayError ? err.message : "The assistant couldn't respond right now.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-6 dark:border-slate-800 dark:bg-[#0f172a]/80">
      <h3 className="font-semibold text-slate-900 dark:text-white">AI Assistant</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Ask about your {emails.length} loaded messages — powered by the same AI Assistant backend as the app.
      </p>

      <div className="mt-4 max-h-96 space-y-3 overflow-y-auto">
        {messages.length === 0 && (
          <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
            Try: &ldquo;Do I have any unread emails from real people today?&rdquo;
          </p>
        )}
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
              m.role === "user"
                ? "ml-auto bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
            }`}
          >
            {m.content}
          </div>
        ))}
        {loading && <p className="text-sm text-slate-400 dark:text-slate-500">Thinking…</p>}
      </div>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-4 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about your inbox…"
          className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
        />
        <button
          type="button"
          onClick={send}
          disabled={loading}
          className="rounded-full bg-violet-600 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-500 disabled:opacity-60"
        >
          Send
        </button>
      </div>
    </div>
  );
}
